import { Component, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';

import { translate } from '@jsverse/transloco';

import {
  SentimentItem,
  ConfigurationService,
} from 'app/providers/semantria-api/configuration.service';
import { EvaluationService } from 'app/providers/semantria-api/evaluation-service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';

import { SentimentPolarityRange } from 'app/providers/storage-api/project.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';
import { ConfigurationDetailsTabBase, Highlight } from '../configuration-details-tab-base';
import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-sentiment',
  templateUrl: './configuration-sentiment.component.html',
  styleUrls: ['./configuration-sentiment.component.scss'],
  standalone: false,
})
export class ConfigurationSentimentComponent
  extends ConfigurationDetailsTabBase<SentimentItem>
  implements OnInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  editingSentiment: SentimentItem;

  originalWeight: number;
  originalName: string;

  isSaving: boolean;

  weightRanges: SentimentPolarityRange[];
  sliderLimits: { min; max } = {
    min: -1,
    max: 1,
  };

  getHighlightText = (doc: DocumentAnalysis, documentText: string, itemValues: string[]) => {
    const highlights: Highlight[] = [];
    if (doc.phrases) {
      doc.phrases
        .filter((p) => p.type === 'detected')
        .forEach((phrase) => {
          const phraseMatch = itemValues.filter((val) =>
            new RegExp(val.replace(/"/g, ''), 'i').test(phrase.title)
          );
          if (phraseMatch.length > 0) {
            let toolTip = translate('configuration-sentiment-component.tooltip-base', {
              score: phrase.sentiment_score,
            });
            if (phrase.is_intensified) {
              toolTip = toolTip.concat(translate('configuration-sentiment-component.intensified'));
            }
            if (phrase.is_negated) {
              toolTip = toolTip.concat(translate('configuration-sentiment-component.negated'));
            }
            highlights.push({
              offset: phrase.offset,
              length: phrase.length,
              tooltipText: toolTip,
              offsetType: 'char',
            });
          }
        });
    }

    super.setDocumentHitIcon(!!highlights.length, doc.id);

    return super.highlightTextStringFromHighlights(highlights, documentText);
  };

  constructor(
    protected route: ActivatedRoute,
    protected configVersionTracker: ConfigurationVersionTrackerService,
    private configService: ConfigurationService,
    private evaluationService: EvaluationService
  ) {
    super(route, configVersionTracker);

    this.itemType = 'Sentiment Phrase';

    this.loadItems = this.configService.getSentimentPhrases.bind(this.configService);
    this.createItem = this.configService.createSentimentPhrase.bind(this.configService);
    this.saveItem = this.configService.saveSentimentPhrase.bind(this.configService);
    this.deleteItem = this.configService.deleteSentimentPhrase.bind(this.configService);
    this.deleteAllItems = this.configService.deleteAllSentimentPhrases.bind(this.configService);
    this.evaluateItem = this.evaluationService.evaluateDocuments.bind(this.evaluationService);
    this.getEvaluationConfig = this.configService.getEvaluationConfigId.bind(this.configService);
    this.getItemByClonedFrom = this.configService.getSentimentPhraseByClonedFrom.bind(
      this.configService
    );
    this.deleteEvaluationConfig = this.configService.deleteConfiguration.bind(this.configService);
  }

  ngOnInit() {
    this.weightRanges = SentimentUtils.getDefaultSentimentRanges('feature', 3);
  }

  getItemHighlightValue(item) {
    return [item.name];
  }

  onItemEditing($event: SentimentItem) {
    this.editingSentiment = $event;

    if ($event) {
      this.editingSentiment.weight = Utils.roundHalfAway($event.weight) || 0.0;
      if (this.editingSentiment.weight < -1) {
        this.sliderLimits = {
          min: this.editingSentiment.weight,
          max: -this.editingSentiment.weight,
        };
      } else if (this.editingSentiment.weight > 1) {
        this.sliderLimits = {
          min: -this.editingSentiment.weight,
          max: this.editingSentiment.weight,
        };
      } else {
        this.sliderLimits = {
          min: -1,
          max: 1,
        };
      }
      this.originalWeight = $event.weight;
      this.originalName = $event.name;
    }
  }

  onItemSaving($event) {
    this.isSaving = $event;
  }

  onWeightChanged(value: number) {
    this.editingSentiment.weight = value;
    this.editor.editorTemplateHasChanges.set(+this.editingSentiment.weight !== this.originalWeight);
  }

  highlightTextStringFromHighlights(highlights: Highlight[], documentText: string): string {
    const highlightPrefix = '<span class="basic-highlight">';
    const highlightPostFix = '</span>';
    const highlightOffset = highlightPrefix.length + highlightPostFix.length;
    highlights.sort((a, b) => {
      return a.offset - b.offset;
    });

    let highlightCount = 0;
    let currentOffset = 0;
    let hightlightedText;
    highlights.forEach((highlight) => {
      currentOffset = highlightCount * highlightOffset + highlight.offset;
      hightlightedText = documentText.substr(currentOffset, highlight.length);
      documentText =
        documentText.substr(0, currentOffset) +
        documentText.substr(currentOffset + highlight.length);
      documentText =
        documentText.substr(0, currentOffset) +
        highlightPrefix +
        hightlightedText +
        highlightPostFix +
        documentText.substr(currentOffset);
      highlightCount += 1;
    });
    return documentText;
  }

  canTestItem() {
    return true;
  }
}
